import { Fragment, useMemo } from 'react';
import {
  Link as RouterLink,
  useLocation,
  useResolvedPath,
} from 'react-router-dom';
import type { PageLayoutProps } from '@backstage/frontend-plugin-api';
import {
  BreadcrumbEntry,
  useBreadcrumbEntries,
} from '@backstage/frontend-plugin-api';
import { Breadcrumb, Heading } from '@internal/kern-ux';

/** Page tabs as KERN navigation with `aria-current`. */
const PageTabs = ({ tabs }: { tabs: NonNullable<PageLayoutProps['tabs']> }) => {
  const { pathname } = useLocation();
  return (
    <nav className="vf-page__tabs" aria-label="Unternavigation">
      <ul className="vf-page__tablist">
        {tabs.map(tab => (
          <li key={tab.id}>
            <RouterLink
              to={tab.href}
              className="vf-page__tab"
              aria-current={pathname.startsWith(tab.href) ? 'page' : undefined}
            >
              {tab.label}
            </RouterLink>
          </li>
        ))}
      </ul>
    </nav>
  );
};

/**
 * KERN header of every plugin page.
 *
 * Replaces Backstage UI's `PluginHeader`: breadcrumbs, heading (exactly one h1 per page),
 * actions and tabs — all with KERN markup instead of BUI components.
 */
export const KernPageLayout = (props: PageLayoutProps) => {
  const { title, noHeader, titleLink, headerActions, tabs, children } = props;
  const parentPath = useResolvedPath('.').pathname.replace(/\/$/, '');
  const { items: breadcrumbs } = useBreadcrumbEntries();

  const resolvedTabs = useMemo(
    () =>
      tabs?.map(tab => ({
        ...tab,
        href: tab.href.startsWith('/')
          ? tab.href
          : `${parentPath}/${tab.href}`.replace(/\/{2,}/g, '/'),
      })),
    [tabs, parentPath],
  );

  if (noHeader) {
    return <>{children}</>;
  }

  const content = (
    <div className="vf-page">
      <div className="vf-page__header">
        {breadcrumbs.length > 1 && (
          <Breadcrumb
            items={breadcrumbs.map(entry => ({
              label: entry.label,
              href: entry.href,
            }))}
          />
        )}
        <div className="vf-page__title-row">
          {title && <Heading type="large" headerElement="h1" title={title} />}
          {headerActions && headerActions.length > 0 && (
            <div className="vf-page__actions">
              {/* Actions arrive as an array from the extension and need keys. */}
              {headerActions.map((action, index) => (
                <Fragment key={index}>{action}</Fragment>
              ))}
            </div>
          )}
        </div>
        {resolvedTabs && resolvedTabs.length > 0 && (
          <PageTabs tabs={resolvedTabs} />
        )}
      </div>
      <div className="vf-page__content">{children}</div>
    </div>
  );

  if (!title) {
    return content;
  }

  return (
    <BreadcrumbEntry
      entry={{ label: title, href: titleLink ?? (parentPath || '/') }}
    >
      {content}
    </BreadcrumbEntry>
  );
};
